/**
 * Functions to transform raw API data into Gantt task objects.
 * This file is kept separate from gantt-utils.tsx to avoid pulling in
 * Bryntum runtime dependencies for code that only needs these transforms.
 */

import { authManager } from "@/lib/managers/auth-manager";
import type { CachedEquipment } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime/date";
import { timeParse } from "d3-time-format";
import { getCalendarName } from "./gantt-calendar";
import { TaskType, type Task, type projectStatus } from "./prop-types";

// NOTE: CalendarModel is imported as TYPE ONLY - this won't trigger Bryntum module loading
import type { CalendarModel } from "@bryntum/gantt-thin";

export const sortPriorityLevels = {
   [TaskType.PROJECT]: 0,
   [TaskType.ASSIGNMENT]: 1,
   [TaskType.EQUIPMENT_ASSIGNMENT]: 2,
   [TaskType.REQUEST]: 3,
   [TaskType.EQUIPMENT_REQUEST]: 4,
   [TaskType.CATEGORY]: 5,
   [TaskType.SUBCATEGORY]: 6,
};

export const projectStatuses: projectStatus[] = [
   { id: 0, name: "Active" },
   { id: 1, name: "Inactive" },
   { id: 2, name: "Pending" },
];

/**
 * Converts a float time to a string (e.g., 14.5 to "14:30:00")
 */
export const timeFloatToString = (time: number | null | undefined): string | undefined => {
   if (typeof time === "number") {
      const hours = Math.floor(time).toString().padStart(2, "0");
      const minutes = Math.floor((time % 1) * 60)
         .toString()
         .padStart(2, "0");
      return `${hours}:${minutes}:00`;
   }
   return time ?? undefined;
};

export const rawAssignmentToTask = ({
   assignment,
   person,
   project,
   jobTitles,
}: {
   assignment: any;
   person: any;
   project: any;
   jobTitles?: any[];
}) => {
   const dateParse = timeParse("%Y-%m-%d");
   const jobTitle =
      person.job_title ?? jobTitles?.find((jobTitle: any) => jobTitle?.id === person?.job_title_id);

   const startDate =
      typeof assignment.start_day === "string"
         ? dateParse(assignment.start_day)
         : assignment.start_day;
   let endDate =
      typeof assignment.end_day === "string" ? dateParse(assignment.end_day) : assignment.end_day;
   endDate = new Date(endDate.setHours(23, 59, 59, 999));

   const firstName = person.first_name ?? person.name.first;
   const lastName = person.last_name ?? person.name.last;
   const lastNamesFirst = authManager.authedUser()?.preferences()?.displayLastNamesFirst();

   const assignmentTask: Task = {
      id: assignment.id,
      type: TaskType.ASSIGNMENT,
      startDate,
      endDate,
      startTime: assignment.start_time,
      endTime: assignment.end_time,
      name: lastNamesFirst ? `${lastName}, ${firstName}` : `${firstName} ${lastName}`,
      projectId: project.id,
      projectColor: project.color,
      projectName: project.name,
      projectJobNumber: project.job_number,
      parentCategoryId: assignment.cost_code_id,
      parentCategoryName: assignment.cost_code?.name,
      parentSubcategoryId: assignment.label_id,
      parentSubcategoryName: assignment.label?.name,
      jobTitleId: jobTitle?.id,
      jobTitleColor: jobTitle?.color,
      jobTitleName: jobTitle?.name,
      workDays: assignment.work_days,
      calendar: getCalendarName(
         assignment,
         TaskType.ASSIGNMENT,
         person.time_off?.length > 0,
      ) as CalendarModel & string,
      manuallyScheduled: true,
      sortPriority: sortPriorityLevels[TaskType.ASSIGNMENT],
      categoryId: assignment.cost_code_id ?? assignment.category_id ?? null,
      subcategoryId: assignment.label_id ?? assignment.subcategory_id ?? null,
      resourceId: person.id,
      overtime: assignment.overtime ?? false,
      status: assignment.status,
      percentAllocated: assignment.percent_allocated,
      resourceName: firstName + " " + lastName,
      employeeNumber: person.employee_number ?? null,
      phoneNumber: person.phone ?? null,
      resourceAddress1: person.address_1 ?? null,
      resourceAddress2: person.address_2 ?? null,
      resourceCityTown: person.city_town ?? null,
      resourceHourlyWage: person.hourly_wage ?? null,
      resourceStateProvince: person.state_province ?? null,
      resourceZipcode: person.zipcode ?? null,
      instructionText: assignment.instruction_text,
      workScopeText: assignment.work_scope_text,
      ...(assignment.segments && {
         segments:
            typeof assignment.segments === "string"
               ? JSON.parse(assignment.segments)
               : assignment.segments,
      }),
   };
   return assignmentTask;
};

export const rawEquipmentAssignmentToTask = ({
   equipmentItem,
   project,
   equipment,
}: {
   equipmentItem: any;
   project: any;
   equipment: CachedEquipment[];
}) => {
   const dateParse = timeParse("%Y-%m-%d");
   const currentEquipment = equipment.find(
      (item: CachedEquipment) => item.id == equipmentItem.resource_id,
   );

   const startDate =
      typeof equipmentItem.start_day === "string"
         ? dateParse(equipmentItem.start_day)
         : equipmentItem.start_day;
   let endDate =
      typeof equipmentItem.end_day === "string"
         ? dateParse(equipmentItem.end_day)
         : equipmentItem.end_day;
   endDate = new Date(endDate.setHours(23, 59, 59, 999));

   const equipmentAssignmentTask: Task = {
      id: equipmentItem.id,
      type: TaskType.EQUIPMENT_ASSIGNMENT,
      startDate,
      endDate,
      startTime: equipmentItem.start_time,
      endTime: equipmentItem.end_time,
      name: currentEquipment?.equipment_name ?? "Equipment Name",
      projectId: project.id,
      projectColor: project.color,
      projectName: project.name,
      projectJobNumber: project.job_number,
      parentCategoryId: equipmentItem.cost_code_id,
      parentCategoryName: equipmentItem.cost_code?.name,
      parentSubcategoryId: equipmentItem.label_id,
      parentSubcategoryName: equipmentItem.label?.name,
      percentAllocated: equipmentItem.percent_allocated,
      jobTitleName: currentEquipment?.type_name ?? "Equipment Type",
      equipmentTypeName: currentEquipment?.type_name ?? "Equipment Type",
      workDays: equipmentItem.work_days,
      calendar: getCalendarName(equipmentItem, TaskType.EQUIPMENT_ASSIGNMENT) as CalendarModel &
         string,
      manuallyScheduled: true,
      sortPriority: sortPriorityLevels[TaskType.EQUIPMENT_ASSIGNMENT],
      categoryId: equipmentItem.cost_code_id ?? equipmentItem.category_id ?? null,
      subcategoryId: equipmentItem.label_id ?? equipmentItem.subcategory_id ?? null,
      resourceId: equipmentItem.resource_id ?? null,
   };

   return equipmentAssignmentTask;
};

export const rawRequestToTask = ({
   request,
   project,
   jobTitles,
}: {
   request: any;
   project: any;
   jobTitles?: any[];
}) => {
   try {
      const dateParse = timeParse("%Y-%m-%d");
      const jobTitle =
         request.job_title ??
         jobTitles?.find(
            (jobTitle: any) => jobTitle.id == (request.job_title_id ?? request.position_id),
         );

      const isDetachedDay =
         typeof request.start_day === "number" && String(request.start_day).length === 8;
      if (isDetachedDay) {
         request.start_day = getAttachedDate(request.start_day);
         request.end_day = getAttachedDate(request.end_day);
      } else if (typeof request.start_day === "string" && typeof request.end_day === "string") {
         request.start_day = dateParse(request.start_day)!;
         request.end_day = dateParse(request.end_day)!;
      } else {
         request.start_day = new Date(request.start_day);
         request.end_day = new Date(request.end_day);
      }

      const requestTask: Task = {
         id: request.id,
         type: TaskType.REQUEST,
         startDate: request.start_day,
         endDate: new Date(request.end_day.setHours(23, 59, 59, 999)), // request bar should end at the end of the day
         startTime: timeFloatToString(request.start_time),
         endTime: timeFloatToString(request.end_time),
         name: "Request",
         projectId: project.id,
         projectColor: project.color,
         projectName: project.name,
         projectJobNumber: project.job_number,
         parentCategoryId: request.cost_code_id,
         parentCategoryName: request.cost_code?.name,
         parentSubcategoryId: request.label_id,
         parentSubcategoryName: request.label?.name,
         percentAllocated: request.percent_allocated,
         jobTitleColor: jobTitle?.color,
         jobTitleName: jobTitle?.name,
         workDays: request.work_days,
         calendar: getCalendarName(request, TaskType.REQUEST) as CalendarModel & string,
         manuallyScheduled: true,
         sortPriority: sortPriorityLevels[TaskType.REQUEST],
         categoryId: request.cost_code_id ?? request.category_id ?? null,
         subcategoryId: request.label_id ?? request.subcategory_id ?? null,
         instructionText: request.instruction_text,
         workScopeText: request.work_scope_text,
         tagIds: request.tag_ids,
      };
      return requestTask;
   } catch (error) {
      console.error("error in rawRequestToTask:", error);
   }
};

export const rawEquipmentRequestToTask = ({
   request,
   project,
   equipmentTypes,
}: {
   request: any;
   project: any;
   equipmentTypes?: any[];
}) => {
   try {
      const dateParse = timeParse("%Y-%m-%d");
      const equipmentType = equipmentTypes?.find(
         (equipmentType: any) => equipmentType.id === request.type_id,
      );

      const isDetachedDay =
         typeof request.start_day === "number" && String(request.start_day).length === 8;
      if (isDetachedDay) {
         request.start_day = getAttachedDate(request.start_day);
         request.end_day = getAttachedDate(request.end_day);
      } else if (typeof request.start_day === "string" && typeof request.end_day === "string") {
         request.start_day = dateParse(request.start_day)!;
         request.end_day = dateParse(request.end_day)!;
      } else {
         request.start_day = new Date(request.start_day);
         request.end_day = new Date(request.end_day);
      }

      const requestTask: Task = {
         id: request.id,
         type: TaskType.EQUIPMENT_REQUEST,
         startDate: request.start_day,
         endDate: new Date(request.end_day.setHours(23, 59, 59, 999)),
         startTime: timeFloatToString(request.start_time),
         endTime: timeFloatToString(request.end_time),
         name: "Equipment Request",
         projectId: project.id,
         projectColor: project.color,
         projectName: project.name,
         projectJobNumber: project.job_number,
         parentCategoryId: request.cost_code_id,
         parentCategoryName: request.cost_code?.name,
         parentSubcategoryId: request.label_id,
         parentSubcategoryName: request.label?.name,
         percentAllocated: request.percent_allocated,
         jobTitleName: equipmentType?.name,
         typeId: request.type_id,
         equipmentTypeName: equipmentType?.name,
         workDays: request.work_days,
         calendar: getCalendarName(request, TaskType.EQUIPMENT_REQUEST) as CalendarModel & string,
         manuallyScheduled: true,
         sortPriority: sortPriorityLevels[TaskType.EQUIPMENT_REQUEST],
         categoryId: request.cost_code_id ?? request.category_id ?? null,
         subcategoryId: request.label_id ?? request.subcategory_id ?? null,
      };
      return requestTask;
   } catch (error) {
      console.error("error in rawEquipmentRequestToTask:", error);
   }
};
